import { supabase } from '@/lib/supabase';

/**
 * PRODUCTION ERROR MONITORING (client side)
 * -----------------------------------------
 * Every browser-side failure is posted to the `observability` edge function,
 * which forwards it to Sentry (the DSN stays server side, never in the bundle)
 * and mirrors it into `mkt_security_events` so the admin panel can show it even
 * when Sentry is unreachable.
 *
 * Captured automatically once `installErrorMonitoring()` has run:
 *   • window.onerror                  uncaught runtime errors
 *   • window.onunhandledrejection     rejected promises (most fetch failures)
 *   • React render crashes            via <ErrorBoundary>
 *   • edge function failures          via invokeEdge() in lib/edgeInvoke.ts
 *   • auth + payment failures         via captureAuthError / capturePaymentError
 *
 * Deliberate design rules:
 *   1. Reporting NEVER throws and never blocks the UI (fire and forget).
 *   2. Identical messages are collapsed for 60s, and the session is capped at
 *      25 reports, so a render loop cannot flood Sentry or the database.
 *   3. Browser-extension and third-party noise is dropped before sending.
 */

/** Bumped on every production deploy; used as the Sentry release tag. */
export const APP_RELEASE = 'giggone@2026-09-18';

export type ReportKind = 'frontend' | 'auth' | 'payment' | 'crm' | 'email' | 'edge' | 'upload';
export type ReportLevel = 'fatal' | 'error' | 'warning' | 'info';

interface ReportInput {
  message: string;
  kind?: ReportKind;
  level?: ReportLevel;
  area?: string;
  stack?: string;
  name?: string;
  handled?: boolean;
  context?: Record<string, unknown>;
}

const MAX_PER_SESSION = 25;
const DEDUPE_MS = 60_000;

let sent = 0;
let installed = false;
const seen = new Map<string, number>();

/** Noise we refuse to pay for: extensions, injected scripts, cancelled loads. */
const IGNORED = [
  'ResizeObserver loop',
  'Non-Error promise rejection captured',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'The play() request was interrupted',
  'Load failed',
  'NetworkError when attempting to fetch resource',
];

const isNoise = (message: string) => IGNORED.some((n) => message.toLowerCase().includes(n.toLowerCase()));

const shouldSend = (key: string) => {
  if (sent >= MAX_PER_SESSION) return false;
  const now = Date.now();
  const last = seen.get(key) || 0;
  if (now - last < DEDUPE_MS) return false;
  seen.set(key, now);
  return true;
};

/** Fire-and-forget report. Safe to call from anywhere, including catch blocks. */
export const captureError = (input: ReportInput): void => {
  try {
    const message = (input.message || '').toString().slice(0, 1000);
    if (!message || isNoise(message)) return;

    const key = `${input.kind || 'frontend'}:${message}`;
    if (!shouldSend(key)) return;
    sent++;

    const payload = {
      kind: input.kind || 'frontend',
      level: input.level || 'error',
      area: input.area || 'app',
      message,
      name: input.name || 'Error',
      stack: input.stack ? String(input.stack).slice(0, 6000) : undefined,
      handled: input.handled !== false,
      release: APP_RELEASE,
      environment: 'production',
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      context: input.context ?? {},
    };

    void supabase.functions.invoke('observability', { body: payload }).catch(() => {});
  } catch {
    /* a reporter that can crash is worse than none */
  }
};

/** Convenience wrapper for unknown thrown values. */
export const captureException = (
  error: unknown,
  meta: { kind?: ReportKind; area?: string; level?: ReportLevel; context?: Record<string, unknown> } = {}
): void => {
  const err = error as { message?: string; name?: string; stack?: string } | null;
  captureError({
    message: err?.message ? String(err.message) : String(error),
    name: err?.name,
    stack: err?.stack,
    kind: meta.kind,
    area: meta.area,
    level: meta.level,
    context: meta.context,
  });
};

/** Authentication failures (sign-in, session refresh, role checks). */
export const captureAuthError = (message: string, context: Record<string, unknown> = {}): void =>
  captureError({ message, kind: 'auth', area: 'authentication', context });

/** Payment / credit failures — always fatal, they cost money. */
export const capturePaymentError = (message: string, context: Record<string, unknown> = {}): void =>
  captureError({ message, kind: 'payment', area: 'payments', level: 'fatal', context });

/**
 * Storage / upload failures (server-side only — never user validation errors
 * like "too big" or "wrong type"). Counted by the `alert-watch` monitor, which
 * emails the admins when uploads start failing for real users.
 */
export const captureUploadError = (
  message: string,
  context: { bucket: string; stage: string; status?: number | string; [k: string]: unknown }
): void => captureError({ message: `upload_failed: ${message}`.slice(0, 900), kind: 'upload', area: 'storage', level: 'error', context });

/** Attach the global browser handlers. Idempotent. */
export const installErrorMonitoring = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const err = (event as ErrorEvent).error as Error | undefined;
    captureError({
      message: err?.message || (event as ErrorEvent).message || 'window.onerror',
      name: err?.name || 'WindowError',
      stack: err?.stack,
      handled: false,
      context: {
        source: (event as ErrorEvent).filename,
        line: (event as ErrorEvent).lineno,
        column: (event as ErrorEvent).colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = (event as PromiseRejectionEvent).reason as { message?: string; name?: string; stack?: string };
    captureError({
      message: reason?.message ? String(reason.message) : `Unhandled rejection: ${String(reason)}`,
      name: reason?.name || 'UnhandledRejection',
      stack: reason?.stack,
      handled: false,
      area: 'promise',
    });
  });
};
